import {
    CalendarDate,
    ClientGenderOptions,
    ClientMaritalStatuses,
    PlacementId,
} from "@sp-crm/core";
import { DateOfBirth } from "components/age-birthday/date-of-birth";
import { DuplicateRecordBanner } from "components/shared/duplicate-record-banner";
import { Checkbox } from "components/ui/checkbox";
import { ContactNameInput } from "components/ui/contact-name-input";
import { CrmPersona } from "components/ui/persona";
import React, { FunctionComponent } from "react";
import { useFeature } from "store/selectors/hooks";
import util from "../../../../util";
import { Header } from "../../../header";
import { Setting } from "../../../setting";
import ClientBiographicalField from "../client-biographical-field";

type ClientBasicInfo = {
    id: PlacementId;
    name: string;
    preferredName: string;
    email: string;
    phone: string;
    cellPhone: string;
    birthday: CalendarDate | null;
    isBirthdayFake: boolean | null;
    gender: string;
    weight: string;
    height: string;
    maritalStatus: string;
};

type PrimaryClientInfoProps = {
    clientBasicInfo: ClientBasicInfo;
    clientSameAsBestContact: boolean;
    showClientIntakeWeight: boolean;
    showClientIntakeHeight: boolean;
    emailOptOut: boolean;
    hasAdditionalClient: boolean;
    now: Date;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
    onFieldChange: (fieldName: string, newValue: any) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
    onMultiFieldChange: (newValues: any) => void;
};

const PrimaryClientInfo: FunctionComponent<PrimaryClientInfoProps> = props => {
    const placementDetailVNext = useFeature("placementDetailVNext");

    const updateAge = (ageStr: string) => {
        let parsedAge = parseInt(ageStr, 10);
        if (isNaN(parsedAge) || parsedAge < 0 || parsedAge > 300) {
            parsedAge = null;
        }

        const birthday = parsedAge ? util.date.yearsAgo(props.now, parsedAge) : null;
        const delta = {
            birthday,
            isBirthdayFake: true,
        };
        props.onMultiFieldChange(delta);
    };

    const updateBirthday = (birthday: string) => {
        const delta = {
            birthday,
            isBirthdayFake: false,
        };

        props.onMultiFieldChange(delta);
    };

    return (
        <div>
            {props.hasAdditionalClient && (
                <Header>
                    <CrmPersona name={props.clientBasicInfo.name} size="large" />
                </Header>
            )}
            {placementDetailVNext ? null : (
                <DuplicateRecordBanner
                    recordType="placement"
                    recordId={props.clientBasicInfo.id}
                    nameKey={props.clientBasicInfo.name}
                />
            )}
            <div className="space-y-2 md:space-y-4">
                <ContactNameInput
                    name={props.clientBasicInfo.name}
                    preferredName={props.clientBasicInfo.preferredName}
                    onCommit={props.onFieldChange}
                    disabled={props.clientSameAsBestContact}
                />
                <div className="flex space-x-4">
                    <div className="flex-1">
                        <ClientBiographicalField
                            type={"select"}
                            label={"Gender"}
                            fieldName={"gender"}
                            initial={props.clientBasicInfo.gender}
                            onFieldChange={props.onFieldChange}
                            options={ClientGenderOptions}
                        />
                    </div>
                    <div className="flex-1">
                        <ClientBiographicalField
                            type={"select"}
                            label={"Marital status"}
                            fieldName={"maritalStatus"}
                            initial={props.clientBasicInfo.maritalStatus}
                            onFieldChange={props.onFieldChange}
                            options={ClientMaritalStatuses}
                        />
                    </div>
                </div>
                <DateOfBirth
                    birthday={props.clientBasicInfo.birthday}
                    isBirthdayFake={props.clientBasicInfo.isBirthdayFake}
                    updateAge={updateAge.bind(this)}
                    updateBirthday={updateBirthday.bind(this)}
                />
                <div className="flex space-x-2 md:space-x-4 items-center">
                    {props.showClientIntakeWeight && (
                        <ClientBiographicalField
                            type="text"
                            label="Weight"
                            fieldName="weight"
                            initial={props.clientBasicInfo.weight}
                            onFieldChange={props.onFieldChange}
                            additionalClassName="small-input-field"
                        />
                    )}
                    {props.showClientIntakeHeight && (
                        <ClientBiographicalField
                            type="text"
                            label="Height"
                            fieldName="height"
                            initial={props.clientBasicInfo.height}
                            onFieldChange={props.onFieldChange}
                            additionalClassName="small-input-field"
                        />
                    )}
                </div>
                <div className="sm:flex sm:space-x-1 md:space-x-2 lg:space-x-4">
                    <ClientBiographicalField
                        type={"phone"}
                        label={"Cell phone number"}
                        fieldName={"cellPhone"}
                        initial={props.clientBasicInfo.cellPhone}
                        onFieldChange={props.onFieldChange}
                        disabled={props.clientSameAsBestContact}
                        entityId={props.clientBasicInfo.id}
                    />
                    <ClientBiographicalField
                        type={"phone"}
                        label={"Phone number (other)"}
                        fieldName={"phone"}
                        initial={props.clientBasicInfo.phone}
                        onFieldChange={props.onFieldChange}
                        entityId={props.clientBasicInfo.id}
                        disabled={props.clientSameAsBestContact}
                    />
                </div>

                <ClientBiographicalField
                    type={"text"}
                    label={"Email"}
                    fieldName={"email"}
                    initial={props.clientBasicInfo.email}
                    onFieldChange={props.onFieldChange}
                    disabled={props.clientSameAsBestContact}
                />

                <Setting name="emailOptOut">
                    <Checkbox
                        checked={props.emailOptOut}
                        onChange={newVal =>
                            props.onFieldChange("emailOptOut", newVal.target.checked)
                        }
                        disabled={props.clientSameAsBestContact}
                        label="Email Opt-out (for bulk/newsletter emails)"
                    />
                </Setting>
            </div>
        </div>
    );
};

export default PrimaryClientInfo;
